import _ from 'underscore';
import React, { useMemo, useContext, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { SelectionZone, Selection, SelectionMode, Stack } from '@fluentui/react';
import type { IObjectWithKey, ISelection } from '@fluentui/react';

import { GoalItem } from './goal-item';
import { campaignGoals } from './consts';
import type { CampaignGoal, GoalSelectionProps } from '../types';

export function GoalSelection(props: GoalSelectionProps) {
  const i18n = useContext(I18nContext);

  const {
    selectedCampaignGoals,
    onCampaignGoalSelectionChange,
    multiSelect,
    isShowIcon,
  } = props;

  const selection = useMemo(() => new Selection<string>({
    canSelectItem: () => true,
    getKey: (item: string) => item,
    selectionMode: multiSelect ? SelectionMode.multiple : SelectionMode.single,
    items: campaignGoals,
    onSelectionChanged: () => {
      onCampaignGoalSelectionChange(selection.getSelection());
    },
  }), [onCampaignGoalSelectionChange, multiSelect]);

  const changeGoalSelection = useCallback((item: CampaignGoal) => {
    const selectedValue = !selection.isKeySelected(item);
    selection.setKeySelected(item, selectedValue, true);
  }, [selection]);

  useEffect(() => {
    if (selection && !_.isEmpty(selectedCampaignGoals)) {
      _.each(selectedCampaignGoals, (item) => {
        selection.setKeySelected(item as string, true, true);
      });
    }
  }, [selection, selectedCampaignGoals]);

  return (
    <div className="campaign-goal-selection">
      <SelectionZone
        selection={selection as unknown as ISelection<IObjectWithKey>}
        onItemInvoked={changeGoalSelection as unknown as (item?: IObjectWithKey, index?: number, event?: Event) => void}
        isSelectedOnFocus={false}
        selectionPreservedOnEmptyClick
      >
        <Stack horizontal tokens={{ childrenGap: 24 }}>
          {_.map(campaignGoals, (campaignGoal, index) => (
            <GoalItem
              key={campaignGoal}
              i18n={i18n}
              id={campaignGoal}
              itemIndex={index}
              selectedCampaignGoals={selectedCampaignGoals}
              isShowIcon={isShowIcon}
            />
          ))}
        </Stack>
      </SelectionZone>
    </div>
  );
}

GoalSelection.propTypes = {
  selectedCampaignGoals: PropTypes.arrayOf(PropTypes.string).isRequired,
  onCampaignGoalSelectionChange: PropTypes.func.isRequired,
  isShowIcon: PropTypes.bool.isRequired,
  multiSelect: PropTypes.bool,
};

GoalSelection.defaultProps = {
  multiSelect: true,
};
